import {CustomFieldType} from '@frontapp/workflows/custom_fields';
import _, {chain} from 'lodash';

import {atIndex} from '../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {AttachmentModel, isAttachmentModel} from '../../../libs/shared/core/src/models/api/attachmentModel';
import {ChannelTypesEnum} from '../../../libs/shared/core/src/models/api/channelCommonTypes';
import {ChannelModel} from '../../../libs/shared/core/src/models/api/channelModel';
import {MessageTypesEnum} from '../../../libs/shared/core/src/models/api/commonMessageModel';
import {ContactSourcesEnum} from '../../../libs/shared/core/src/models/api/contactModel';
import {ConversationTeamStatusesEnum} from '../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {ConversationTrackerStatusesEnum} from '../../../libs/shared/core/src/models/api/conversationTrackerModel';
import {InboxModel} from '../../../libs/shared/core/src/models/api/inboxModel';
import {RecipientModel} from '../../../libs/shared/core/src/models/api/recipientModel';
import {TeammateModel} from '../../../libs/shared/core/src/models/api/teammateModel';
import {
  ApplicationParsedNamespace,
  globalNamespace,
  parseNamespaceForApplication,
} from '../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {
  ApplicationAttachment,
  ApplicationChannel,
  ApplicationChannelType,
  ApplicationComment,
  applicationCompanyId,
  ApplicationContact,
  ApplicationContactHandleType,
  ApplicationConversation,
  ApplicationConversationStatus,
  ApplicationConversationType,
  ApplicationCustomAttribute,
  ApplicationInbox,
  ApplicationInboxId,
  ApplicationLink,
  ApplicationRecipient,
  ApplicationSingleConversation,
  ApplicationTag,
  ApplicationTeammate,
  ApplicationTopic,
} from '../../applications/bridge/v2/types/bridgeTypesV2';
import {CardModel} from '../../models/api/cardModel';
import {CommentModel} from '../../models/api/commentModel';
import {ConversationBaseModel} from '../../models/api/conversationModel';
import {CustomFieldModel} from '../../models/api/customFieldModel';
import {LegacyApplicationModel} from '../../models/api/legacyApplicationModel';
import {TagModel} from '../../models/api/tagModel';
import {TopicModel} from '../../models/api/topicModel';
import {buildPublicId} from '../../models/applications/publicIdTypes';
import {buildPublicUid} from '../../models/applications/publicUidTypes';
import {DataState} from '../../store/data/dataState';
import {FindTeammate, FindTeammateByEmail} from '../../store/data/teammates/teammatesSelectors';
import {findCardForContactInNamespace} from './contactHelpers';
import {isLegacyApplicationCustomPlugin} from './legacyApplicationHelpers';

/*
 * Types.
 */

/**
 * Temporary interface for finding teammates during lazy teammates migration.
 * TODO: Remove this and use Pick<DataState, 'teammates'> again.
 */
export interface FindTeammateData {
  findTeammate: FindTeammate;
  findTeammateByEmail: FindTeammateByEmail;
}

/*
 * Preferences.
 */

// TODO: Improve application preferences logic.
export function serializePreferencesForApplication(user: TeammateModel, application: LegacyApplicationModel) {
  const userPreferences = application.namespacePreferences[user.namespace];
  const globalPreferences = application.namespacePreferences.global;

  // TEMPORARY: Expose global preferences for custom plugin applications only.
  if (isLegacyApplicationCustomPlugin(application)) {
    return {
      ...globalPreferences?.preferences,
      ...userPreferences?.preferences,
    };
  }

  return {...userPreferences?.preferences};
}

/*
 * Teammate.
 */

export function serializeTeammateForApplication(teammate: TeammateModel): ApplicationTeammate {
  return {
    id: buildPublicId('teammate', teammate.id),
    name: `${teammate.givenName} ${teammate.familyName}`,
    username: teammate.alias,
    email: teammate.email,
  };
}

/*
 * Inbox.
 */

export function serializeInboxForApplication(inbox: InboxModel): ApplicationInbox {
  const owner = parseNamespaceForApplication(inbox.namespace);

  if (owner.type === globalNamespace) {
    throw new Error('Inbox should not have global namespace');
  }

  return {
    id: buildPublicId('inbox', inbox.id),
    ownerId: buildPublicId(owner.type, owner.id),
    name: inbox.name,
  };
}

/*
 * Channel.
 */

export function serializeChannelForApplication(
  {inboxes}: Pick<DataState, 'inboxes'>,
  channel: ChannelModel,
): ApplicationChannel {
  const inbox = inboxes.byId[channel.inboxId];
  if (!inbox) {
    throw new Error('The channel inbox could not be found.');
  }

  return {
    id: buildPublicId('channel', channel.id),
    type: channelTypeToApplicationChannelType[channel.type],
    inbox: serializeInboxForApplication(inbox),
    name: channel.displayName,
    address: channel.address,
  };
}

/*
 * Conversation.
 */

export function serializeConversationForApplication(
  data: Pick<DataState, 'inboxes' | 'tags' | 'contacts' | 'cards' | 'customFields' | 'topics'> &
    FindTeammateData,
  userId: number,
  conversation: ConversationBaseModel,
): ApplicationConversation {
  const {findTeammate, inboxes, tags} = data;
  const {teamStatus, assigneeId, inboxIds, tagIds, topicIds} = conversation;
  const tracker = conversation.trackers[userId];

  // Status.
  const status = tracker ? tracker.status : teamStatus;

  // Assignee.
  const assignee = assigneeId ? findTeammate(assigneeId) : undefined;

  // Recipient.
  const recipient = (tracker && tracker.recipient) || conversation.recipient;

  // Inboxes.
  const applicationInboxes = chain(inboxIds)
    .map((inboxId) => {
      const inbox = inboxes.byId[inboxId];
      return inbox && serializeInboxForApplication(inbox);
    })
    .compact()
    .value();

  // Tags.
  const applicationTags = chain(tagIds)
    .map((tagId) => {
      const tag = tags.byId[tagId];
      return tag && serializeTagForApplication(data, tag);
    })
    .compact()
    .value();

  // Topics.
  const topics = _(topicIds)
    .map((topicId) => data.topics.byId[topicId])
    .compact()
    .value();

  return {
    id: buildPublicId('conversation', conversation.id),
    type: messageTypeToApplicationConversationType(conversation.messageType),
    status: conversationStatusToApplicationConversationStatus(status),
    subject: conversation.subject,
    blurb: conversation.summary,
    assignee: assignee && serializeTeammateForApplication(assignee),
    recipient: recipient && serializeRecipientForApplication(data, recipient, conversation.namespace),
    inboxes: applicationInboxes,
    tags: applicationTags,
    topics: topics.map(serializeTopicForApplication),
    links: topics.map(serializeTopicForApplication),
  };
}

export function serializeSingleConversationForApplication(
  data: Pick<DataState, 'inboxes' | 'tags' | 'contacts' | 'cards' | 'customFields' | 'topics'> &
    FindTeammateData,
  userId: number,
  conversation: ConversationBaseModel,
  draftMessageUid: string | undefined,
): ApplicationSingleConversation {
  return {
    ...serializeConversationForApplication(data, userId, conversation),
    draftId: draftMessageUid ? buildPublicUid('draft', draftMessageUid) : undefined,
  };
}

/*
 * Recipient.
 */

export function serializeRecipientForApplication(
  data: Pick<DataState, 'contacts' | 'cards' | 'customFields'> & FindTeammateData,
  recipient: RecipientModel,
  namespace: string,
  channels?: ReadonlyArray<ChannelModel>,
): ApplicationRecipient {
  const {contacts, cards} = data;
  const contact = recipient && contacts[recipient.contactUid];
  if (!contact) {
    throw new Error('A contact for this recipient could not be found.');
  }

  const card = findCardForContactInNamespace(contact, cards.byId, namespace);
  const channel = channels && channels.find((c) => c.id === contact.channelId);
  return {
    name: channel ? channel.displayName : recipient.name,
    handle: channel ? channel.sendAddress : contact.handle,
    contact: card && serializeCardForApplication(data, card),
    type: contact.source,
  };
}

/*
 * Card.
 */

export function serializeCardForApplication(
  data: Pick<DataState, 'contacts' | 'customFields'> & FindTeammateData,
  card: CardModel,
): ApplicationContact {
  const {contacts, customFields} = data;
  const handles = chain(card.contactUids)
    .map((uid) => contacts[uid])
    .compact()
    .map((contact) => ({
      type: contactSourceToApplicationContactHandleType[contact.source],
      handle: contact.handle,
    }))
    .value();

  const customAttributes = chain(card.customFieldAttributes)
    .map((attribute) => {
      const customField = customFields.byId[attribute.id];
      return customField && serializeCustomFieldValueForApplication(customField, attribute.value, data);
    })
    .compact()
    .value();

  return {
    id: buildPublicId('card', card.id),
    name: card.name,
    description: card.description,
    groupIds: card.groupIds.map((groupId) => buildPublicId('cardGroup', groupId)),
    handles,
    customAttributes,
  };
}

function serializeCustomFieldValueForApplication(
  customField: CustomFieldModel,
  value: string,
  {findTeammateByEmail}: FindTeammateData,
): ApplicationCustomAttribute | undefined {
  const baseCustomField = {
    id: buildPublicId('customField', customField.id),
    name: customField.name,
  };

  // Format the value according to the field type.
  switch (customField.type) {
    case CustomFieldType.BOOLEAN:
      return {
        ...baseCustomField,
        type: 'boolean',
        value: value === 'true',
      };

    case CustomFieldType.DATETIME:
      return {
        ...baseCustomField,
        type: 'datetime',
        value: new Date(Number(value)),
      };

    case CustomFieldType.NUMBER:
      return {
        ...baseCustomField,
        type: 'number',
        value: Number(value),
      };

    case CustomFieldType.STRING:
      return {
        ...baseCustomField,
        type: 'string',
        value,
      };

    case CustomFieldType.ENUM:
      return {
        ...baseCustomField,
        type: 'enum',
        value,
      };

    case CustomFieldType.TEAMMATE: {
      const teammate = findTeammateByEmail(value);

      if (!teammate) {
        return undefined;
      }

      return {
        ...baseCustomField,
        type: 'teammateId',
        value: buildPublicId('teammate', teammate.id),
      };
    }

    case CustomFieldType.INBOX:
      return {
        ...baseCustomField,
        type: 'inboxId',
        value: value as ApplicationInboxId,
      };

    case CustomFieldType.TEAMMATE_LIST: // TODO(laurentmeyer 2023-08-14): should not be used currently, to update once it is.
    default:
      return {
        ...baseCustomField,
        type: 'unknown',
        value,
      };
  }
}

/*
 * Attachment.
 */

export function serializeAttachmentForApplication(attachment: AttachmentModel): ApplicationAttachment {
  const {linkId, name, contentType, size, isInline, cid} = attachment;
  if (!linkId) {
    throw new Error('LinkID not found on attachment.');
  }

  return {
    id: buildPublicId('attachment', linkId),
    name,
    contentType,
    size,
    inlineCid: isInline ? cid : undefined,
  };
}

/*
 * Comment.
 */

export function serializeCommentForApplication(
  teammate: TeammateModel,
  comment: CommentModel,
): ApplicationComment | undefined {
  if (!comment.id) {
    return undefined;
  }
  return {
    id: buildPublicId('comment', comment.id),
    author: serializeTeammateForApplication(teammate),
    content: {
      body: comment.text,
      attachments: comment.attachments.filter(isAttachmentModel).map(serializeAttachmentForApplication),
    },
    createdAt: new Date(comment.createdAt),
    lastEditedAt: (comment.lastEditedAt && new Date(comment.lastEditedAt)) || undefined,
  };
}

/*
 * Tag.
 */

function serializeTagForApplication({tags}: Pick<DataState, 'tags'>, tag: TagModel) {
  const parentTags: Array<TagModel> = [];
  let {parentTagId} = tag;

  while (parentTagId) {
    const parentTag = tags.byId[parentTagId];
    if (!parentTag) {
      break;
    }

    parentTags.push(parentTag);
    parentTagId = parentTag.parentTagId;
  }

  return serializeTagAndParentsForApplication(tag, parentTags);
}

export function serializeTagAndParentsForApplication(
  tag: TagModel,
  parentTags: ReadonlyArray<TagModel>,
): ApplicationTag {
  const parentTag = atIndex(parentTags, 0);
  const owner = parseNamespaceForApplication(tag.namespace);
  const ownerId = buildOwnerId(owner);

  return {
    id: buildPublicId('tag', tag.id),
    ownerId,
    name: tag.name,
    parentTag: parentTag && serializeTagAndParentsForApplication(parentTag, parentTags.slice(1)),
  };
}

/*
 * Topic.
 */

export function serializeTopicForApplication(topic: TopicModel): ApplicationTopic | ApplicationLink {
  return {
    id: buildPublicId('topic', topic.id),
    type: 'web',
    externalUrl: topic.externalLink,
    name: topic.name,
    appUrl: topic.appLink,
  };
}

/*
 * Conversion maps.
 */

const channelTypeToApplicationChannelType: {[key in ChannelTypesEnum]: ApplicationChannelType} = {
  [ChannelTypesEnum.CUSTOM]: 'custom',
  [ChannelTypesEnum.GMAIL]: 'email',
  [ChannelTypesEnum.IMAP]: 'email',
  [ChannelTypesEnum.OFFICE365]: 'email',
  [ChannelTypesEnum.SMTP]: 'email',
  [ChannelTypesEnum.FACEBOOK]: 'facebook',
  [ChannelTypesEnum.LAYER_ANON]: 'frontChat',
  [ChannelTypesEnum.FRONT_CHAT]: 'frontChat',
  [ChannelTypesEnum.FRONT_MAIL]: 'email',
  [ChannelTypesEnum.FORM]: 'frontForm',
  [ChannelTypesEnum.GOOGLE_PLAY]: 'googlePlay',
  [ChannelTypesEnum.INTERCOM]: 'intercom',
  [ChannelTypesEnum.SMOOCH]: 'smooch',
  [ChannelTypesEnum.TALKDESK]: 'talkdesk',
  [ChannelTypesEnum.TRULY]: 'truly',
  [ChannelTypesEnum.TWILIO]: 'twilio',
  [ChannelTypesEnum.TWITTER]: 'twitter',
  [ChannelTypesEnum.TWITTER_DM]: 'twitterDm',
  [ChannelTypesEnum.TWILIO_WHATSAPP]: 'whatsapp',
  [ChannelTypesEnum.YALO_WHATSAPP]: 'whatsapp',
};

const contactSourceToApplicationContactHandleType: {
  [key in ContactSourcesEnum]: ApplicationContactHandleType;
} = {
  [ContactSourcesEnum.EMAIL]: 'email',
  [ContactSourcesEnum.PHONE]: 'phone',
  [ContactSourcesEnum.TWITTER]: 'twitter',
  [ContactSourcesEnum.TWITTER_DM]: 'twitter',
  [ContactSourcesEnum.CUSTOM]: 'custom',
  [ContactSourcesEnum.FACEBOOK]: 'facebook',
  [ContactSourcesEnum.INTERCOM]: 'intercom',
  [ContactSourcesEnum.SMOOCH]: 'smooch',
  [ContactSourcesEnum.LAYER]: 'frontChat',
  [ContactSourcesEnum.FRONT_CHAT]: 'frontChat',
  [ContactSourcesEnum.GOOGLE_PLAY]: 'googlePlay',
};

function conversationStatusToApplicationConversationStatus(
  status: ConversationTeamStatusesEnum | ConversationTrackerStatusesEnum,
): ApplicationConversationStatus {
  switch (status) {
    case ConversationTeamStatusesEnum.OPEN:
    case ConversationTrackerStatusesEnum.INBOX:
      return 'open';

    case ConversationTeamStatusesEnum.ARCHIVED:
    case ConversationTrackerStatusesEnum.ARCHIVED:
      return 'archived';

    case ConversationTeamStatusesEnum.TRASHED:
    case ConversationTrackerStatusesEnum.TRASHED:
      return 'trashed';

    case ConversationTeamStatusesEnum.SPAM:
    case ConversationTrackerStatusesEnum.SPAM:
      return 'spam';

    default:
      throw new Error(`Unable to serializse conversation status ${status}.`);
  }
}

function messageTypeToApplicationConversationType(type: MessageTypesEnum): ApplicationConversationType {
  switch (type) {
    case MessageTypesEnum.INTERNAL:
      return 'internal';

    case MessageTypesEnum.EMAIL:
      return 'email';

    case MessageTypesEnum.TWEET:
      return 'tweet';

    case MessageTypesEnum.TWEET_DM:
      return 'twitterDm';

    case MessageTypesEnum.CALL:
      return 'phoneCall';

    case MessageTypesEnum.SMS:
      return 'sms';

    case MessageTypesEnum.GOOGLE_PLAY:
      return 'googlePlay';

    case MessageTypesEnum.FACEBOOK:
      return 'facebook';

    case MessageTypesEnum.INTERCOM:
      return 'intercom';

    case MessageTypesEnum.SMOOCH:
      return 'smooch';

    case MessageTypesEnum.CUSTOM:
      return 'custom';

    // Do not make a distinction between the different whatsapp messages.
    case MessageTypesEnum.YALO_WHATSAPP:
    case MessageTypesEnum.WHATSAPP:
      return 'whatsapp';

    // Backward compatibility, Layer was previously used for Front Chat.
    case MessageTypesEnum.LAYER:
    case MessageTypesEnum.FRONT_CHAT:
      return 'frontChat';

    default:
      throw new Error(`Unable to serializse conversation message type ${type}.`);
  }
}

/*
 * Build public ID.
 */

function buildOwnerId(owner: ApplicationParsedNamespace) {
  return owner.type === globalNamespace ? applicationCompanyId : buildPublicId(owner.type, owner.id);
}
